import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useGetAllRuns, useGetSchedules, useGetAllSourceTables } from '@/api/control/control-plane/control-plane';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AlertCircle, CheckCircle2, Clock, TrendingUp, ExternalLink, Database, MinusCircle } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { QueryErrorState } from '@/components/common/QueryErrorState';
import type { RunMetadataDto, AllSourceTableDto, ScheduleDto } from '@/api/control/missionImpactAPI.schemas';
import { calculateNextSyncTime, formatNextSyncTime } from '@/utils/nextSyncTime';

interface ProfileRunSummaryCardProps {
  /** The organization ID */
  orgId: string;
  /** The profile ID to fetch runs for */
  profileId: string;
  /** The profile name to display in the card header */
  profileName: string;
  /** Whether the profile is enabled */
  isEnabled?: boolean;
  /** The profile's burst expiry (ISO-8601 UTC), or null when no burst is active. */
  burstExpiresUtc?: string | null;
}

interface RunStats {
  total: number;
  completed: number;
  failed: number;
  inProgress: number;
  skipped: number;
}

/**
 * Determines if a run state is considered "in progress"
 */
function isInProgressState(state: string | undefined): boolean {
  const inProgressStates = ['Extracting', 'Loading', 'WaitingForTargetLease', 'WaitingForSourceCapacity', 'Scheduled'];
  return state ? inProgressStates.includes(state) : false;
}

/**
 * Calculates statistics from a list of runs
 */
function calculateStats(runs: RunMetadataDto[]): RunStats {
  return runs.reduce<RunStats>(
    (stats, run) => {
      stats.total++;
      if (run.state === 'Completed') {
        stats.completed++;
      } else if (run.state === 'Failed') {
        stats.failed++;
      } else if (isInProgressState(run.state)) {
        stats.inProgress++;
      } else if (run.state === 'Skipped') {
        stats.skipped++;
      }
      return stats;
    },
    { total: 0, completed: 0, failed: 0, inProgress: 0, skipped: 0 }
  );
}

/**
 * Card component that displays run statistics for a specific profile over the last 24 hours
 */
export function ProfileRunSummaryCard({ orgId, profileId, profileName, isEnabled = true, burstExpiresUtc = null }: ProfileRunSummaryCardProps) {
  const { t } = useLanguage();
  const [sourceTables, setSourceTables] = useState<AllSourceTableDto[]>([]);
  const [hasSourceTablesError, setHasSourceTablesError] = useState(false);
  const [sourceTablesError, setSourceTablesError] = useState<unknown>(null);

  // Calculate 24 hours ago - useMemo to prevent recalculation on every render
  const startDate = useMemo(() => {
    const twentyFourHoursAgo = new Date();
    twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
    return twentyFourHoursAgo.toISOString();
  }, []);

  // Fetch schedules for this profile to calculate next sync time
  const { data: schedulesData, error: schedulesError, refetch: refetchSchedules } = useGetSchedules(orgId, profileId, {
    query: {
      enabled: !!orgId && !!profileId,
      staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    },
  });

  const schedules: ScheduleDto[] = schedulesData?.data ?? [];

  // Calculate next sync time (burst-aware: reflects the 5-minute cadence while a burst is active)
  const nextSyncTime = calculateNextSyncTime(schedules, new Date(), burstExpiresUtc);

  // Fetch source tables for this profile
  const { mutate: fetchSourceTables } = useGetAllSourceTables({
    mutation: {
      onSuccess: (response) => {
        setSourceTables(response.data.items ?? []);
        setHasSourceTablesError(false);
        setSourceTablesError(null);
      },
      onError: (error) => {
        console.error('Failed to load source tables:', error);
        setHasSourceTablesError(true);
        setSourceTablesError(error);
      },
    },
  });

  // Fetch runs using query hook
  const { data: runsData, isLoading: isLoadingRuns, error: runsError, refetch: refetchRuns } = useGetAllRuns(
    orgId,
    {
      pageSize: 100,
      profileIds: [profileId],
      startDate,
    },
    {
      query: {
        enabled: !!orgId && !!profileId,
        staleTime: 1000 * 60 * 5, // Cache for 5 minutes
      },
    }
  );

  const allRuns = runsData?.data.items ?? [];
  const stats = calculateStats(allRuns);

  // Fetch source tables on mount
  useEffect(() => {
    if (orgId && profileId) {
      fetchSourceTables({
        orgId,
        data: {
          profileIds: [profileId],
          onlyEnabled: true,
        },
      });
    }
  }, [orgId, profileId]);

  // Function to retry fetching all data
  const retryFetch = () => {
    setHasSourceTablesError(false);
    setSourceTablesError(null);

    // Retry runs
    void refetchRuns();

    // Retry source tables
    fetchSourceTables({
      orgId,
      data: {
        profileIds: [profileId],
        onlyEnabled: true,
      },
    });

    // Retry schedules
    if (schedulesError) {
      void refetchSchedules();
    }
  };

  const isLoading = isLoadingRuns;

  // Find the most recent run (runs are ordered newest first)
  const lastRun = allRuns.length > 0 ? allRuns[0] : null;

  // Format the last sync time
  const formatLastSyncTime = (run: RunMetadataDto | null): string => {
    if (!run) return t.dashboard.runSummary.noRecentSyncs;

    const timestamp = run.startedUtc ?? run.scheduledUtc;
    if (!timestamp) return t.dashboard.runSummary.noRecentSyncs;

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return t.dashboard.runSummary.justNow;
    if (diffMins < 60) return `${String(diffMins)} ${diffMins === 1 ? t.dashboard.runSummary.minuteAgo : t.dashboard.runSummary.minutesAgo}`;
    if (diffHours < 24) return `${String(diffHours)} ${diffHours === 1 ? t.dashboard.runSummary.hourAgo : t.dashboard.runSummary.hoursAgo}`;
    if (diffDays < 7) return `${String(diffDays)} ${diffDays === 1 ? t.dashboard.runSummary.dayAgo : t.dashboard.runSummary.daysAgo}`;

    return date.toLocaleDateString();
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg">{profileName}</CardTitle>
            <CardDescription>{t.dashboard.runSummary.last24Hours}</CardDescription>
          </div>
          <Link to={`/dashboard/orgs/${orgId}/profiles/${profileId}`}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <ExternalLink className="h-4 w-4" />
            </Button>
          </Link>
        </div>
        {!isLoading && (
          <div className="space-y-2 pt-2">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground cursor-help w-fit">
                    <Database className="h-3 w-3" />
                    <span>{sourceTables.length} {sourceTables.length !== 1 ? t.dashboard.runSummary.tables : t.dashboard.runSummary.table}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {sourceTables.length === 0 ? (
                    <p>{t.dashboard.runSummary.noTablesConfigured}</p>
                  ) : (
                    <div className="max-w-xs">
                      <p className="font-semibold mb-1">{t.dashboard.runSummary.sourceTables}</p>
                      <ul className="list-disc list-inside">
                        {sourceTables.map((table) => (
                          <li key={table.id}>{table.logicalName}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <div className="text-xs text-muted-foreground">
              {t.dashboard.runSummary.lastSync} {formatLastSyncTime(lastRun)}
            </div>
            <div className="text-xs text-muted-foreground">
              {t.dashboard.runSummary.nextSync} {isEnabled ? formatNextSyncTime(nextSyncTime) : t.profiles.sync.notScheduledDisabled}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <>
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-16" />
            </div>
            <div className="space-y-3">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-full" />
            </div>
          </>
        ) : runsError || hasSourceTablesError || schedulesError ? (
          <QueryErrorState
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- mixing nullable Error and boolean types
            error={runsError || sourceTablesError || schedulesError}
            message={
              runsError
                ? t.errors.profiles.failedToLoadRuns
                : hasSourceTablesError
                ? t.errors.profiles.failedToLoadSourceTables
                : t.errors.profiles.failedToLoadSchedules
            }
            onRetry={retryFetch}
          />
        ) : (
          <>
            {/* Total Runs */}
            <div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <TrendingUp className="h-4 w-4" />
                <span>{t.dashboard.runSummary.totalRuns}</span>
              </div>
              <div className="text-3xl font-bold">{stats.total}</div>
            </div>

            {/* Stats Breakdown */}
            <div className="space-y-2 pt-2 border-t">
              {/* Successful Runs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-success" />
                  <span className="text-muted-foreground">{t.dashboard.runSummary.successful}</span>
                </div>
                <span className="font-semibold text-success">{stats.completed}</span>
              </div>

              {/* Failed Runs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <AlertCircle className="h-4 w-4 text-error" />
                  <span className="text-muted-foreground">{t.dashboard.runSummary.failed}</span>
                </div>
                <span className="font-semibold text-error">{stats.failed}</span>
              </div>

              {/* In Progress Runs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-blue-600" />
                  <span className="text-muted-foreground">{t.dashboard.runSummary.inProgress}</span>
                </div>
                <span className="font-semibold text-blue-600">{stats.inProgress}</span>
              </div>

              {/* Skipped Runs */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <MinusCircle className="h-4 w-4 text-neutral" />
                  <span className="text-muted-foreground">{t.dashboard.runSummary.skipped}</span>
                </div>
                <span className="font-semibold text-neutral">{stats.skipped}</span>
              </div>
            </div>

            {/* Success Rate */}
            {stats.total > 0 && (
              <div className="pt-2 border-t">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t.dashboard.runSummary.successRate}</span>
                  <span className="font-semibold">
                    {((stats.total - stats.failed) / stats.total * 100).toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
